import {useState} from 'react';

import {useNonMountEffect} from './effectHelpers';

interface Options {
  timeout?: number; // in milliseconds
}

type ReturnType<T> = [
  debouncedValue: T,
  value: T,
  setValue: (value: T) => void,
  setValueUnbounced: (value: T) => void,
];

export const useDebouncedValue = <T>(
  initialValue: T,
  options: Options = {timeout: 250}
): ReturnType<T> => {
  const [value, setValue] = useState(initialValue);
  const [debouncedValue, setDebouncedValue] = useState(value);

  useNonMountEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedValue(value);
    }, options.timeout);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [value, options.timeout]);

  const setValueUnbounced = (resetValue: T) => {
    setValue(resetValue);
    setDebouncedValue(resetValue);
  };

  return [debouncedValue, value, setValue, setValueUnbounced];
};
